/**
 * "이 돈으로 며칠 버티나" — tide-over 앱의 계산 로직을 Dada Entry 위에서 다시 짠 것.
 *
 * ─────────────────────────────────────────────────────────────────
 * 이 모듈이 지키는 원칙 (tide-over CLAUDE.md §1·§2 그대로)
 *
 *   입력의 원자는 지출이 아니라 잔고다. 미래는 달력에, 과거는 잔고에.
 *
 *   • 과거 지출은 입력하지 않는다. 잔고를 옮겨 적는 순간 그 사이의 변동 지출이
 *     정산된 것으로 본다.
 *   • 예측하지 않는다. 카드의 숫자는 "이 날까지 쓸 수 있는 한도"(`limitOn`)이고,
 *     달력의 "예상 잔고"(`projectedBalanceOn`)는 적어 둔 입출금만 더한 값이다.
 *     변동 지출을 추정하지 않는 것은 누락이 아니라 정의다.
 *
 * ─────────────────────────────────────────────────────────────────
 * Dada Entry 위에서 도는 방식
 *
 * tide-over 원본은 { kind, amount, schedule: once|monthly|every|span } 였다.
 * Dada는 이미 { kind: 'money', money: { type, amountMinor }, startDate,
 * endDate, recurrence } 로 같은 것을 표현한다. 스키마를 늘리지 않고 매핑한다:
 *
 *   tide-over          →  Dada Entry (kind: 'money')
 *   once(date)         →  startDate = date, endDate = null, recurrence = null
 *   span(start, end)   →  startDate = start, endDate = end
 *   monthly(day)       →  recurrence.freq = 'monthly' (day는 startDate에서 유도)
 *   every(N days)      →  recurrence.freq = 'daily', interval = N
 *   weekly             →  every 7 과 같다 (원본에 없던 케이스)
 *
 * money.type 의 sign(+1 = 입금, -1 = 출금, 0 = 흐름에 반영 안 함)이 tide-over의
 * income/expense 부호를 겸한다. save · free 는 sign 0 이라 이 계산에서 빠진다.
 * 카드 사용(`flow: 'cardSpend'`)도 0 이다 — 현금은 카드 대금 날에 나간다 (`ledger.cashSignOf`).
 *
 * ─────────────────────────────────────────────────────────────────
 * 예정과 실제 거래 (`ledger.ts`)
 *
 * 지금까지의 항목은 전부 **예정**이고 계산도 그대로다. 실제 거래가 들어오면서 두 가지가
 * 늘었다.
 *
 *   1. 실제 거래가 확정한 예정 회차는 **건너뛴다.** 그 회차의 돈은 실제 거래가 대신
 *      센다. 둘 다 세면 같은 월세가 두 번 빠진다. 실제 거래를 지우면 회차가 돌아온다.
 *   2. 실제 거래는 날짜 구간이 아니라 **잔고에 들어 있는가**로 센다
 *      (`ledger.balanceInclusion`). 들어 있으면 이미 잔고의 일부이고, 아니면 아직 잔고
 *      밖의 움직임이다. 예정의 경계(`unsettledAfter`)를 쓰지 않는 이유는 같은 날
 *      거래를 날짜로 가를 수 없기 때문이다.
 *
 * 건너뛸 회차의 집합은 **넘겨받은 목록 전체**에서 한 번 만든다. 항목 하나씩 떼어
 * 계산하는 자리(`limitOn`)에서 그 한 건으로 집합을 만들면 늘 비어 버린다.
 */
import { DEFAULT_CURRENCY } from './constants';
import { addDaysISO, daysBetween, localDateOf, normalizeDate, parseDate, toISO } from './date';
import { effectiveEndDate } from './entry';
import { inAnyBudget, reservedOn, reserveProtects } from './budget';
import { cardRemainders, cycleRef, isCardPayment, paymentCycleKey } from './card';
import {
  ambiguousActuals, balanceInclusion, cashSignOf, confirmedKey, confirmedSetOf, isActual,
} from './ledger';
import { isVirtualEntry } from './recurrence';
import type { Account, Budget, Card, DateISO, Entry, Reserve } from './types';

/**
 * 한도에서 미리 빠져 있는 것들.
 *
 * 선택 인자로 둔다 — 예산도 세이브도 없는 계산(대부분의 테스트, `/tide` 이식 전 코드)이
 * 그대로 돌아야 한다. 빈 값이면 예전과 완전히 같은 숫자가 나온다.
 */
export interface Reservations {
  /** 생활비 예산. `max(총액, 쓴 돈)` 만큼 한도에서 자리를 잡는다. */
  budgets?: readonly Budget[];
  /** 세이브. 잔고는 그대로 두고 한도에서만 빠진다. */
  reserves?: readonly Reserve[];
  /**
   * 세이브마다 연결된 실제 지출의 합(P3, `budget.reserveUsageOf`). 한도에서는 확보액이 아니라 남은 금액을 뺀다 —
   * 세이브에서 쓴 돈은 현금 흐름에서 이미 빠지기 때문이다. 연결된 거래 **전량**에서 센 값을 넘긴다(계산 창의 거래만
   * 세면 창 밖의 지난 사용이 빠진다). 없으면 사용액 0 — P3 이전과 같은 숫자다.
   */
  reserveUsage?: ReadonlyMap<string, number>;
  /**
   * 신용카드(P4)와 카드에 걸린 거래 **전량**(`money.cardId != null`). 둘 다 주어야 카드 미결제가 한도에서 빠진다.
   * 카드 미결제는 계산 창이 아니라 이 목록에서 센다 — 창 밖의 지난 사용도 아직 갚지 않았으면 빚이다.
   * 없으면 미결제 0 — P4 이전과 같은 숫자다.
   */
  cards?: readonly Card[];
  cardEntries?: readonly Entry[];
}

const NO_BUDGETS: readonly Budget[] = [];
const NO_RESERVES: readonly Reserve[] = [];

/**
 * 계산에 화면용 목록이 들어왔다.
 *
 * 배선 실수지 사용자 데이터 문제가 아니다 — `virtual` 은 저장 경로에 없어서
 * `expandEntry()` 말고는 켤 수 없다. 조용히 지나가면 같은 입출금을 여러 번 세어
 * 금액이 몇 배로 부풀고, 그 숫자가 그럴듯해서 아무도 눈치채지 못한다.
 */
export class TideInputError extends Error {
  constructor(entryId: string) {
    super(
      `금액 계산에 화면용 발생분이 들어왔습니다 (${entryId}). `
      + 'materialize() 결과가 아니라 원본 항목을 넘겨야 합니다 — '
      + '발생분을 넣으면 반복이 한 번 더 전개돼 같은 입출금을 여러 번 셉니다.',
    );
    this.name = 'TideInputError';
  }
}

/**
 * 계산 입력 검사.
 *
 * tide 로 들어오는 모든 목록이 여기를 지난다. 반복 항목은 `occurrences()` 가 직접
 * 전개하므로, 이미 전개된 사본을 받으면 안 된다.
 */
function assertOriginals(entries: readonly Entry[]): void {
  for (const e of entries) {
    if (isVirtualEntry(e)) throw new TideInputError(e.id);
  }
}

/**
 * 이 계산이 다룰 수 있는 범위인가.
 *
 * ── 지원 범위 ──────────────────────────────────────────────
 *   계좌는 여럿이어도 된다. 한도는 그 합에서 잰다.
 *   통화는 **한 번에 하나**다. 환율 변환 기능이 없기 때문이다.
 *
 * 금액은 전부 최소 단위 정수(`amountMinor`)다. KRW 는 원, USD 는 센트라 단위가 다르다.
 * 섞인 채로 더하면 1,000원과 10.00달러가 같은 1,000 이 된다 — 틀린 줄도 모르게 틀린
 * 숫자가 나온다. 그래서 더하지 않고, 계산할 수 없다고 말한다.
 */
export type CurrencyScope =
  | { ok: true; currency: string }
  | { ok: false; currencies: string[] };

export function currencyScopeOf(
  accounts: readonly Account[], entries: readonly Entry[],
  /**
   * 한도에서 빠지는 생활비 · 세이브. 이들의 금액도 한도에 더해지므로 통화가 다르면 같은 이유로
   * 계산을 멈춘다. 보호를 끄거나 보관한 세이브는 한도에 들어가지 않으니 따지지 않는다.
   */
  res: Reservations = {},
): CurrencyScope {
  const seen = new Set<string>();
  for (const a of accounts) {
    if (a.currency) seen.add(a.currency);
  }
  for (const e of entries) {
    // 흐름에 반영되지 않는 항목(세이브·가용)은 한도를 건드리지 않으니 따지지 않는다.
    if (!participates(e)) continue;
    if (e.money?.currency) seen.add(e.money.currency);
  }
  for (const b of res.budgets ?? NO_BUDGETS) {
    if (b.currency) seen.add(b.currency);
  }
  for (const r of res.reserves ?? NO_RESERVES) {
    if (reserveProtects(r) && r.currency) seen.add(r.currency);
  }
  const list = [...seen].sort();
  if (list.length <= 1) return { ok: true, currency: list[0] ?? DEFAULT_CURRENCY };
  return { ok: false, currencies: list };
}

/**
 * 금액을 낼 수 있는가 — 통화와 잔고 반영 판정을 함께 본다.
 *
 *   currency    통화가 섞였다 (`currencyScopeOf`)
 *   unassigned  계좌가 여럿인데, 잔고에 들어 있는지 가를 수 없는 실제 거래가 있다
 *
 * `unassigned` 는 넘겨짚지 않으려고 둔 갈래다. 기준일이 9/1 인 계좌와 9/10 인 계좌가
 * 있고 9/5 거래가 어느 계좌의 것인지 모르면, 한쪽으로 가정한 한도는 그 거래 금액만큼
 * 틀린다. 카드와 달력은 숫자 대신 이유를 적는다. 거래에 계좌를 적거나 잔고를 다시
 * 적어 기준일을 맞추면 풀린다.
 */
export type CalcScope =
  | { ok: true; currency: string }
  | { ok: false; reason: 'currency'; currencies: string[] }
  | { ok: false; reason: 'unassigned'; entries: Entry[] };

export function calcScopeOf(
  accounts: readonly Account[], entries: readonly Entry[], res: Reservations = {},
): CalcScope {
  const cur = currencyScopeOf(accounts, entries, res);
  if (!cur.ok) return { ok: false, reason: 'currency', currencies: cur.currencies };
  const unknown = ambiguousActuals(accounts, entries);
  if (unknown.length > 0) return { ok: false, reason: 'unassigned', entries: unknown };
  return cur;
}

/**
 * 예정 한 건의 하루 발생분. amount 는 그 날 몫(양수).
 * 반복 · 단발은 entry.money.amountMinor 그대로,
 * 기간(span)은 일할 몫(마지막 날에 나머지 몰아 준다).
 */
export interface Occurrence {
  date: DateISO;
  entry: Entry;
  /** 하루 몫. 항상 양수, 원 단위. */
  amountMinor: number;
}

/** 현금 부호. 0 이면 tide 계산에서 제외(세이브·가용 · 카드 사용). */
function signOf(entry: Entry): -1 | 0 | 1 {
  return cashSignOf(entry);
}

/** 이 항목이 tide 흐름에 반영되는가. */
function participates(entry: Entry): boolean {
  return signOf(entry) !== 0;
}

function amountOf(entry: Entry): number {
  return entry.money?.amountMinor ?? 0;
}

/** 기간 항목인가. Dada 는 endDate 로 표현한다. */
function isSpan(entry: Entry): boolean {
  return !!entry.endDate && entry.endDate > entry.startDate;
}

/**
 * (after, through] 구간에 잡히는 예정 입금·출금.
 * 시작은 열려 있고 끝은 닫혀 있다 — "오늘 이후 ~ d일까지"가 그대로 이 모양이다.
 * 오늘까지 지나간 발생분은 애초에 잡히지 않는다 (tide-over 불변식 4).
 */
export function occurrences(
  entries: readonly Entry[], after: DateISO, through: DateISO,
  /** 실제 거래로 확정된 회차. 주지 않으면 `entries` 에서 만든다. */
  confirmed: ReadonlySet<string> = confirmedSetOf(entries),
): Occurrence[] {
  assertOriginals(entries);

  const afterN = normalizeDate(after);
  const throughN = normalizeDate(through);
  if (!afterN || !throughN || afterN >= throughN) return [];

  const raw: Occurrence[] = [];

  for (const entry of entries) {
    if (!participates(entry)) continue;
    // 실제 거래는 예정의 구간 규칙으로 세지 않는다. 잔고 반영 여부로 센다.
    if (isActual(entry)) continue;

    const amount = Math.abs(amountOf(entry));
    if (amount === 0) continue;

    const r = entry.recurrence;

    // 반복이 있으면 반복이 이긴다. endDate 로 span 을 겸하는 항목은 없다고 본다.
    if (r) {
      if (r.freq === 'monthly') {
        addMonthlyOccurrences(raw, entry, amount, afterN, throughN, r.interval, r.until, r.count);
      } else if (r.freq === 'weekly') {
        addEveryOccurrences(raw, entry, amount, afterN, throughN, r.interval * 7, r.until, r.count);
      } else {
        addEveryOccurrences(raw, entry, amount, afterN, throughN, r.interval, r.until, r.count);
      }
      continue;
    }

    if (isSpan(entry)) {
      addSpanOccurrences(raw, entry, amount, afterN, throughN);
      continue;
    }

    // once
    const d = normalizeDate(entry.startDate);
    if (d && d > afterN && d <= throughN) {
      raw.push({ date: d, entry, amountMinor: amount });
    }
  }

  /*
    확정된 회차를 건너뛴다. 기간(span)의 하루 몫은 회차가 아니므로 건너뛰지 않는다 —
    기간 예정은 확정 대상이 아니다 (`confirm.ts`).
  */
  const out = confirmed.size === 0
    ? raw
    : raw.filter((o) => (isSpan(o.entry) && !o.entry.recurrence)
      || !confirmed.has(confirmedKey(o.entry.id, o.date)));

  out.sort((a, b) => a.date.localeCompare(b.date) || a.entry.title.localeCompare(b.entry.title));
  return out;
}

/**
 * 아직 잔고 밖에 있는 실제 거래 가운데 `through` 까지의 것. 날짜 순이다.
 *
 * 하루 몫으로 쪼개지 않는다 — 실제 거래는 한 날짜의 사실이다.
 */
function unreflectedActuals(
  accounts: readonly Account[], entries: readonly Entry[], through: DateISO,
): Occurrence[] {
  const out: Occurrence[] = [];
  for (const entry of entries) {
    if (!isActual(entry) || !participates(entry)) continue;
    const amount = Math.abs(amountOf(entry));
    if (amount === 0) continue;
    const date = normalizeDate(entry.startDate);
    if (!date || date > through) continue;
    // 판정할 수 없는 거래(`ambiguous`)는 센다 — 그때는 화면이 숫자를 내지 않는다
    // (`calcScopeOf`). 계산만 하는 자리에서는 더 작은 한도 쪽이 덜 위험하다.
    if (balanceInclusion(entry, accounts) === 'in') continue;
    out.push({ date, entry, amountMinor: amount });
  }
  return out;
}

/**
 * 기간 예산 (span): 총액을 일할로 깐다. 나머지를 마지막 날에 몰아줘야
 * 마지막 날이 지나는 순간 합이 정확히 총액이 된다.
 */
function addSpanOccurrences(
  out: Occurrence[], entry: Entry, amount: number,
  after: DateISO, through: DateISO,
): void {
  const start = normalizeDate(entry.startDate);
  const end = normalizeDate(effectiveEndDate(entry));
  if (!start || !end) return;

  const spanDays = daysBetween(start, end) + 1;
  if (spanDays <= 0) return;

  const perDay = Math.floor(amount / spanDays);
  const firstCountable = addDaysISO(after, 1);
  const from = start > firstCountable ? start : firstCountable;
  const to = end <= through ? end : through;

  for (let d = from; d <= to; d = addDaysISO(d, 1)) {
    const dayAmount = d === end ? amount - perDay * (spanDays - 1) : perDay;
    out.push({ date: d, entry, amountMinor: dayAmount });
  }
}

/**
 * every N 일마다: anchor(startDate) + k·N 을 돈다.
 * after 보다 뒤인 첫 k 로 바로 점프해 반복이 오래된 항목도 빠르게 잡는다.
 */
function addEveryOccurrences(
  out: Occurrence[], entry: Entry, amount: number,
  after: DateISO, through: DateISO,
  intervalDays: number, until: DateISO | null, count: number | null,
): void {
  if (intervalDays < 1) return;
  const anchor = normalizeDate(entry.startDate);
  if (!anchor) return;

  const gap = daysBetween(anchor, after);
  const kStart = gap >= 0 ? Math.floor(gap / intervalDays) + 1 : 0;
  const limit = count ?? Number.MAX_SAFE_INTEGER;

  for (let k = kStart; k < limit; k++) {
    const date = addDaysISO(anchor, k * intervalDays);
    if (date > through) break;
    if (until && date > until) break;
    if (date > after) out.push({ date, entry, amountMinor: amount });
  }
}

/** monthly: 구간에 걸친 달을 돌며 그 달의 지정일(startDate의 day)을 잡는다. */
function addMonthlyOccurrences(
  out: Occurrence[], entry: Entry, amount: number,
  after: DateISO, through: DateISO,
  interval: number, until: DateISO | null, count: number | null,
): void {
  const anchor = parseDate(entry.startDate);
  if (!anchor) return;
  const day = anchor.getDate();
  const step = Math.max(1, interval);

  const startD = parseDate(after);
  const endD = parseDate(through);
  if (!startD || !endD) return;

  // anchor 로부터 몇 번째 발생인지 세면서 count 상한을 지킨다.
  let k = 0;
  const limit = count ?? Number.MAX_SAFE_INTEGER;

  const anchorYm = anchor.getFullYear() * 12 + anchor.getMonth();
  const startYm = startD.getFullYear() * 12 + startD.getMonth();
  // startYm 에 가장 가까운 anchor + k·step 로 점프한다.
  if (startYm > anchorYm) {
    k = Math.floor((startYm - anchorYm) / step);
  }

  const lastYm = endD.getFullYear() * 12 + endD.getMonth();

  while (k < limit) {
    const targetYm = anchorYm + k * step;
    if (targetYm > lastYm) break;
    const year = Math.floor(targetYm / 12);
    const month0 = targetYm - year * 12;
    // 그 달에 없는 날짜(2월 31일 등)는 말일로 당긴다.
    const lastDay = new Date(year, month0 + 1, 0).getDate();
    const date = toISO(new Date(year, month0, Math.min(day, lastDay)));
    if (until && date > until) break;
    if (date > after && date <= through) {
      out.push({ date, entry, amountMinor: amount });
    }
    k += 1;
  }
}

/** 입금은 +, 출금은 −. */
export function netOf(list: readonly Occurrence[]): number {
  return list.reduce((t, o) => t + signOf(o.entry) * o.amountMinor, 0);
}

export function totalIn(list: readonly Occurrence[]): number {
  return list.reduce((t, o) => (signOf(o.entry) > 0 ? t + o.amountMinor : t), 0);
}

export function totalOut(list: readonly Occurrence[]): number {
  return list.reduce((t, o) => (signOf(o.entry) < 0 ? t + o.amountMinor : t), 0);
}

/** (after, through] 구간의 예정 순액. */
export function netBetween(
  entries: readonly Entry[], after: DateISO, through: DateISO,
  confirmed: ReadonlySet<string> = confirmedSetOf(entries),
): number {
  return netOf(occurrences(entries, after, through, confirmed));
}

/**
 * 머리 숫자의 끝점.
 * 급여일 설정은 없다. 급여도 그냥 예정 입금이고, 다음 예정 입금 전날까지로 잡는다.
 * 기간 예산(span)은 흐름이라 "다음 입금"에서 제외한다 —
 * 매일 조금씩 들어오는 걸 기준 삼으면 끝점이 늘 내일이 된다.
 */
export interface Horizon {
  end: DateISO;
  /** 다음 예정 입금 날. 없으면 null (30일 뒤로 폴백). */
  nextIncome: DateISO | null;
}

const SEARCH_DAYS = 400;
const FALLBACK_DAYS = 30;

export function horizonOf(entries: readonly Entry[], today: DateISO): Horizon {
  // 거르기 전에 본다. 걸러낸 뒤에 검사하면 입금이 하나도 없는 화면용 목록이 통과한다.
  assertOriginals(entries);
  // 다음 **예정** 입금이다. 이미 들어온 돈(실제 거래)은 끝점을 정하지 않고, 그것이
  // 확정한 회차도 다음 입금이 아니다 — 25일 급여가 23일에 들어왔으면 다음은 다음 달이다.
  const confirmed = confirmedSetOf(entries);
  const incomes = entries.filter((e) => signOf(e) > 0 && !isSpan(e) && !isActual(e));
  const upcoming = occurrences(incomes, today, addDaysISO(today, SEARCH_DAYS), confirmed);
  const next = upcoming[0]?.date;
  if (next) return { end: addDaysISO(next, -1), nextIncome: next };
  return { end: addDaysISO(today, FALLBACK_DAYS), nextIncome: null };
}

/**
 * 잔고가 말해 주는 마지막 날 — 가장 최근에 적은 잔고의 기준일.
 *
 * 고르는 순서는 `checkedAt`(순간)으로 정하고 — 하루에 두 번 갈아엎어도 순서가 잡힌다 —
 * 날짜는 그 기록의 `asOf`(벽시계 날짜)로 잡는다.
 */
export function balanceAsOf(accounts: readonly Account[], todayISO: DateISO): DateISO {
  let since = todayISO;
  let sinceTime = -Infinity;
  for (const a of accounts) {
    const t = a.checkedAt ? Date.parse(a.checkedAt) : Date.parse(`${a.asOf}T00:00:00`);
    if (!Number.isFinite(t) || t < sinceTime) continue;
    sinceTime = t;
    // asOf 는 이미 벽시계 날짜다. 없거나 깨졌으면 순간에서 기기 날짜를 뽑는다.
    since = normalizeDate(a.asOf) || localDateOf(a.checkedAt ?? '') || todayISO;
  }
  return since;
}

/**
 * 한도가 세기 시작하는 경계. 이 날 **다음**부터 센다.
 *
 * ── 왜 오늘이 아닌가 ────────────────────────────────────────────
 *
 * 예전에는 오늘 다음부터 셌다(`(오늘, d]`). 그래서 **오늘 날짜로 적은 입출금이 어디에도
 * 반영되지 않았다** — 머리 숫자도, 오늘 셀도, 남은 예정 목록도 그대로였다. 내일 날짜로
 * 적으면 바로 반영되는데 오늘만 사라지니, 적어 둔 사람 입장에서는 저장이 안 된 것처럼 보인다.
 *
 * 경계는 오늘이 아니라 **잔고를 적은 날**이다. 잔고는 그 날의 사실이고, 그 다음에 잡힌
 * 예정은 아직 그 숫자에 들어 있지 않다. 잔고 기준일이 사흘 전이면 그 사흘 사이의 예정도
 * 아직 잔고 밖이므로 함께 세야 한다.
 *
 * 다만 **오늘은 넘지 않는다.** 이 앱은 "과거 지출은 입력하지 않는다" 를 전제로 하므로,
 * 적어 둔 항목은 아직 일어나지 않은 계획이다. 오늘 적은 계획도 아직 앞에 있다.
 * 그래서 잔고를 오늘 적었더라도 오늘 것은 한도에서 뺀다.
 *
 * 정산(`settle`)은 이 경계의 반대쪽 — **어제까지** 를 잔고에 흡수된 것으로 본다.
 * 둘이 겹치지도, 사이가 비지도 않는다.
 */
export function unsettledAfter(accounts: readonly Account[], todayISO: DateISO): DateISO {
  const since = balanceAsOf(accounts, todayISO);
  const yesterday = addDaysISO(todayISO, -1);
  return since < yesterday ? since : yesterday;
}

/**
 * limit(d) = 현재 잔고 + (잔고 기준일 이후 ~ d일까지의 예정 입금 − 예정 출금)
 *
 * "예상 잔고"가 아니라 "이 날까지 쓸 수 있는 한도"다.
 * 기간 예산(span) 규칙: d 가 기간에 들어서는 순간 남은 몫 전체를 예약한다.
 * 일할로 깎으면 기간 안 날들이 매일 줄어드는 숫자로 보인다 — 그 돈은
 * 어차피 기간 동안 묶인 돈이라 다른 지출의 한도에서는 통째로 빼는 게 맞다.
 * 기간 안에서는 상수이고, 마지막 날이 지나도 같은 값이다.
 */
export function limitOn(
  accounts: readonly Account[], entries: readonly Entry[],
  date: DateISO, today: DateISO, res: Reservations = {},
): number {
  assertOriginals(entries);
  // 오늘이 아니라 잔고 기준일 다음부터 센다. 오늘 적은 예정도 아직 잔고 밖이다.
  const from = unsettledAfter(accounts, today);
  const budgets = res.budgets ?? NO_BUDGETS;
  const reserves = res.reserves ?? NO_RESERVES;
  const confirmed = confirmedSetOf(entries);
  const balance = accounts.reduce((s, a) => s + a.balanceMinor, 0);
  let total = balance;
  for (const entry of entries) {
    if (!participates(entry) || isActual(entry)) continue;
    // 예산에서 나가는 지출은 예산 몫 안에서 세어진다. 여기서 또 빼면 두 번 빠진다.
    if (inAnyBudget(entry, budgets)) continue;
    if (isSpan(entry) && !entry.recurrence) {
      const end = normalizeDate(effectiveEndDate(entry));
      const start = normalizeDate(entry.startDate);
      if (start && end && start <= date) {
        total += netBetween([entry], from, end, confirmed);
      }
    } else {
      total += netBetween([entry], from, date, confirmed);
    }
  }
  // 아직 잔고에 들어 있지 않은 실제 거래. 예산에 걸린 것은 예산 몫에서 센다.
  const actuals = unreflectedActuals(accounts, entries, normalizeDate(date))
    .filter((o) => !inAnyBudget(o.entry, budgets));
  total += netOf(actuals);
  // 확보해 둔 돈(생활비 예산 · 세이브)은 쓸 수 있는 한도에서 미리 빠진다.
  total -= reservedOn(budgets, reserves, entries, date, from, accounts, today, res.reserveUsage);
  // 아직 갚지 않은 카드 사용(P4) 가운데 위 흐름에 대금으로 아직 잡히지 않은 몫.
  return total - cardHoldOn(accounts, entries, date, today, res);
}

/*
  ── 카드 미결제 (P4) ──────────────────────────────────────────

  카드로 쓴 돈은 그날 현금이 나가지 않지만(`cashSignOf` 0) 갚아야 할 빚이다. 한도에서는 회차마다 **한 번만** 뺀다.

    회차의 남은 금액 R  = 청구(실제 카드 사용) − 납부(실제 카드 대금)            (`card.cardRemainders`)
    예정 대금 P(d)      = 그 회차를 가리키는 예정 대금 가운데 d 까지 흐름에 잡힌 몫
    카드 보호(d)        = max(0, R − P(d))

  예정 대금은 이미 위의 흐름에서 출금으로 빠졌으므로 보호에서 그만큼 덜어 낸다. 회차 하나가 한도에서 차지하는 자리는
  언제나 max(R, P(d)) 이다 — 사용자가 적은 예정이 크면 그 예정을, 작거나 없으면 실제 빚을 따른다.

  실제 대금을 적으면 R 이 줄고 그 대금이 (잔고 밖이면) 실제 거래로 빠진다. 잔고에 반영하면 잔고에서 빠져 있다.
  어느 쪽이든 같은 돈은 한 번 빠진다 — 적기 전 · 적은 뒤 · 잔고 반영 뒤의 가용이 같다(`card.test.ts`).

  카드 사용을 세이브에 걸면(P3 · P4) 세이브의 남은 금액이 그만큼 줄고 카드 보호가 그만큼 는다. 같은 돈을 지키는
  자리가 세이브에서 카드 빚으로 옮겨 갈 뿐 두 번 빠지지 않는다.
*/

/** `카드id|회차` → 그 회차를 가리키는 예정 대금 가운데 (after, through] 의 합. */
function plannedCardPayments(
  entries: readonly Entry[], after: DateISO, through: DateISO,
  confirmed: ReadonlySet<string>, budgets: readonly Budget[],
): Map<string, number> {
  const out = new Map<string, number>();
  for (const entry of entries) {
    if (!isCardPayment(entry) || isActual(entry) || inAnyBudget(entry, budgets)) continue;
    for (const o of occurrences([entry], after, through, confirmed)) {
      const ref = cycleRef(entry.money!.cardId!, paymentCycleKey(entry, o.date));
      out.set(ref, (out.get(ref) ?? 0) + o.amountMinor);
    }
  }
  return out;
}

/** 카드 미결제 가운데 그 날까지 대금으로 흐름에 잡히지 않은 몫 — 한도에서 따로 빼는 금액. */
export function cardHoldOn(
  accounts: readonly Account[], entries: readonly Entry[], date: DateISO, today: DateISO, res: Reservations = {},
): number {
  if (!res.cards?.length || !res.cardEntries?.length) return 0;
  assertOriginals(res.cardEntries);
  const remainders = cardRemainders(res.cards, res.cardEntries, today);
  if (remainders.size === 0) return 0;
  const from = unsettledAfter(accounts, today);
  const planned = plannedCardPayments(entries, from, normalizeDate(date), confirmedSetOf(entries), res.budgets ?? NO_BUDGETS);
  let hold = 0;
  for (const [ref, r] of remainders) hold += Math.max(0, r.remainingMinor - (planned.get(ref) ?? 0));
  return hold;
}

/**
 * 화면의 '확보' 가운데 카드 미결제 몫 — 그 날의 예상 잔고에 예상 대금으로도 아직 잡히지 않은 빚.
 * `확보 = 예상 잔고 − 가용` 이고, 예상 잔고는 결제일이 지난 예상 대금을 이미 뺐으므로 그만큼 덜어 낸다.
 */
export function cardHeldOn(
  accounts: readonly Account[], entries: readonly Entry[], date: DateISO, today: DateISO, res: Reservations = {},
): number {
  const d = normalizeDate(date);
  const counted = cardDueEstimates(accounts, entries, today, res).reduce((t, c) => (c.date <= d ? t + c.amountMinor : t), 0);
  return cardHoldOn(accounts, entries, date, today, res) - counted;
}

/** 아직 적지 않은 카드 대금의 예상 — 예상 잔고가 결제일에 빼는 금액. */
export interface CardDueEstimate {
  cardId: string;
  cycle: string;
  /** 결제일. 지났으면 오늘(아직 갚지 않은 빚이다). */
  date: DateISO;
  /** 남은 금액 − 그 회차를 가리키는 예정 대금. */
  amountMinor: number;
}

/**
 * 예상 잔고에 넣을 카드 대금. 사용자가 그 회차의 예정 대금을 적었으면 그 예정이 먼저다 — 남은 금액 가운데 예정이
 * 덮지 못한 몫만 결제일에 둔다. 같은 돈을 예정과 예상에서 두 번 빼지 않는다.
 */
export function cardDueEstimates(
  accounts: readonly Account[], entries: readonly Entry[], today: DateISO, res: Reservations = {},
): CardDueEstimate[] {
  if (!res.cards?.length || !res.cardEntries?.length) return [];
  assertOriginals(res.cardEntries);
  const remainders = cardRemainders(res.cards, res.cardEntries, today);
  if (remainders.size === 0) return [];
  const from = unsettledAfter(accounts, today);
  let last = today;
  for (const r of remainders.values()) if (r.payDate > last) last = r.payDate;
  const planned = plannedCardPayments(entries, from, addDaysISO(last, SEARCH_DAYS), confirmedSetOf(entries), res.budgets ?? NO_BUDGETS);
  const out: CardDueEstimate[] = [];
  for (const [ref, r] of remainders) {
    const amount = r.remainingMinor - (planned.get(ref) ?? 0);
    if (amount <= 0) continue;
    const [cardId, cycle] = ref.split('|') as [string, string];
    out.push({ cardId, cycle, date: r.payDate > today ? r.payDate : today, amountMinor: amount });
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * 머리 숫자 — 다음 입금 전날(또는 30일 뒤)까지 **지금 쓸 수 있는** 돈.
 *
 * 구간 안 날짜별 가용의 **최솟값**이다. 예전에는 끝점 하루의 값이었는데, 그때는 가용이 날마다
 * 줄기만 해서 끝점이 곧 최솟값이었다. 생활비 예약이 조회일 기준으로 풀리면서(P1) 가용이 중간에
 * 다시 늘 수 있다 — 10/17 에 끝나는 생활비 200,000 은 10/18 부터 가용으로 돌아온다. 그 늘어난
 * 끝점 값을 머리에 적으면 "생활비 밖에서 지금 250,000 을 써도 된다" 로 읽히고, 그렇게 쓰면
 * 생활비 기간 동안 쓸 돈이 모자란다. 오늘 쓰는 돈은 구간의 **모든** 날의 가용을 줄이므로,
 * 지금 쓸 수 있는 것은 그중 가장 작은 값이다.
 *
 * 가용이 늘어나는 날은 생활비가 풀리는 날뿐이다(입금은 구간 밖이다). 그래서 최솟값은
 * 끝점과, 구간 안에서 끝나는 각 생활비의 종료일 가운데 하나에 있다 — 그 날들만 잰다.
 */
export function headlineLimit(
  accounts: readonly Account[], entries: readonly Entry[], today: DateISO,
  res: Reservations = {},
): number {
  return limitOn(accounts, entries, headlineDate(accounts, entries, today, res), today, res);
}

/**
 * 머리 숫자를 정하는 날 — 구간 안에서 가용이 가장 작은 날. 같으면 앞날을 고른다.
 * 카드가 "그 날의 예상 잔고 − 확보 = 쓸 수 있는 돈" 을 같은 날로 적도록 따로 내놓는다.
 */
export function headlineDate(
  accounts: readonly Account[], entries: readonly Entry[], today: DateISO,
  res: Reservations = {},
): DateISO {
  const end = horizonOf(entries, today).end;
  const checkpoints = new Set<DateISO>([end]);
  for (const b of res.budgets ?? NO_BUDGETS) {
    if (b.endDate >= today && b.endDate < end) checkpoints.add(b.endDate);
  }
  let best: { date: DateISO; value: number } | null = null;
  for (const d of [...checkpoints].sort()) {
    const value = limitOn(accounts, entries, d, today, res);
    if (!best || value < best.value) best = { date: d, value };
  }
  return best ? best.date : end;
}

// ---------- 잔고 · 예상 잔고 · 가용 한도 ----------

/*
  네 숫자는 서로 다른 질문에 답한다. 섞지 않는다.

    확인 잔고      사용자가 적은 계좌 잔고의 합. 생활비 · 세이브를 만들어도 변하지 않는다
                  (`confirmedBalance`). 기준일이 지났으면 "지금 잔고" 가 아니다 — 화면이
                  기준일을 함께 적는다.
    예상 잔고      확인 잔고 + 그 날까지 적어 둔 현금 입출금(`projectedBalanceOn`).
                  예정 입출금, 아직 잔고에 안 들어간 실제 거래를 더한다. 카드 사용은 0,
                  카드 대금은 출금. 생활비 · 세이브는 **빼지 않는다** — 내부 배분이지
                  출금이 아니다. 적지 않은 미래 소비는 모른다(예측하지 않는다).
    남은 생활비    총 예산 − 실제로 쓴 돈 (`budget.budgetState`). 예정은 쓴 돈이 아니다.
    생활비 외 가용  그 날까지 쓸 수 있는 돈 = `limitOn`. 확인 잔고에서 출발해 일반 흐름을
                  더하고, 생활비 몫(`pocketReserve`) · 세이브 · 기간 예정의 남은 몫을 뺀다.
                  생활비에서 쓴 돈은 몫 안에서 세어지고 일반 흐름에서 빠지므로 두 번 빠지지
                  않는다.

  예) 잔고 250,000 · 생활비 200,000 · 다른 거래 없음
      확인 250,000 · 예상 250,000 · 남은 생활비 200,000 · 가용 50,000
      생활비에서 40,000 을 쓰고 아직 잔고에 반영 전:  예상 210,000 · 남은 160,000 · 가용 50,000
      잔고를 210,000 으로 다시 적음:                확인 210,000 · 예상 210,000 · 가용 50,000
*/

/** 확인 잔고 — 계좌에 적힌 잔고의 합. */
export function confirmedBalance(accounts: readonly Account[]): number {
  return accounts.reduce((s, a) => s + a.balanceMinor, 0);
}

/**
 * 예상 잔고 — 확인 잔고에 `date` 까지 적어 둔 현금 입출금을 더한 값.
 *
 * 경계는 한도와 같다(`unsettledAfter`). 예정은 (경계, date] 의 회차를, 실제 거래는 아직
 * 잔고 밖인 것 가운데 date 까지를 더한다. 생활비에 걸린 지출도 **현금이므로 더한다** —
 * 가용 한도와 달리 생활비 몫을 따로 잡아 두지 않기 때문에 두 번 빠질 일이 없다.
 * 기간 예정은 하루 몫으로 그 날까지만 더한다(한도는 기간에 들어서는 순간 남은 몫 전체를
 * 잡아 두지만, 그것은 쓸 수 있는 돈의 규칙이지 통장의 움직임이 아니다).
 */
export function projectedBalanceOn(
  accounts: readonly Account[], entries: readonly Entry[], date: DateISO, today: DateISO,
  /** 카드(P4)만 본다 — 아직 적지 않은 대금을 결제일에 뺀다(`cardDueEstimates`). 생활비 · 세이브는 빼지 않는다. */
  res: Reservations = {},
): number {
  assertOriginals(entries);
  const from = unsettledAfter(accounts, today);
  const d = normalizeDate(date);
  const cardDue = cardDueEstimates(accounts, entries, today, res)
    .reduce((t, c) => (c.date <= d ? t + c.amountMinor : t), 0);
  return confirmedBalance(accounts)
    + netOf(occurrences(entries, from, d))
    + netOf(unreflectedActuals(accounts, entries, d))
    - cardDue;
}

/** 한 날짜의 잔고 보기. 화면은 이 하나로 셋을 함께 적는다. */
export interface BalanceView {
  date: DateISO;
  /** 확인 잔고와 그 기준일(가장 최근에 적은 잔고의 날짜). */
  confirmed: number;
  asOf: DateISO;
  /** 그 날의 예상 잔고. */
  projected: number;
  /** 그 날까지의 생활비 외 가용 한도 (`limitOn`). */
  available: number;
  /** 예상 잔고 가운데 생활비 · 세이브 · 기간 예정으로 확보돼 있는 몫 = projected − available. */
  held: number;
}

export function balanceViewOn(
  accounts: readonly Account[], entries: readonly Entry[], date: DateISO, today: DateISO,
  res: Reservations = {},
): BalanceView {
  const projected = projectedBalanceOn(accounts, entries, date, today, res);
  const available = limitOn(accounts, entries, date, today, res);
  return {
    date,
    confirmed: confirmedBalance(accounts),
    asOf: balanceAsOf(accounts, today),
    projected,
    available,
    held: projected - available,
  };
}

/**
 * 정산.
 *
 * diff = 새로 적은 잔고 − (직전 잔고 + 그 사이 지나간 예정 입금 − 예정 출금)
 *
 * 과거 지출을 입력하지 않아도 이 한 줄로 변동 지출이 전부 정산된다.
 * checkedAt 이 없으면(예전 asOf 만 있는 데이터) asOf 를 자정으로 본다.
 */
export interface Settlement {
  /**
   * 정산 구간의 시작 — 잔고를 적은 **날짜**.
   *
   * 시각이 아니라 날짜다. 예정 항목은 `startDate` 만 갖고 시각이 없으므로, 정산은
   * 애초에 날짜 단위로만 정확할 수 있다. `checkedAt`(순간)은 여러 계좌 중 어느 기록이
   * 가장 최근인지 고르는 데만 쓰고, 구간의 경계로는 쓰지 않는다.
   */
  since: DateISO;
  /**
   * 확정 차액을 낼 수 있는가. false 면 금액으로 보여 주면 안 된다.
   *
   * 세 갈래로 false 가 된다 — `reason` 이 어느 쪽인지 알려 준다.
   */
  complete: boolean;
  /**
   * complete 가 false 인 이유.
   *
   *   coverage  잔고 기준일이 자료 구간보다 앞선다. 계산 구독은 오늘 기준 고정 구간
   *             (뒤로 1달)만 받으므로, 그 사이의 **비반복** 입출금이 목록에 없다.
   *             (반복 항목은 전량 구독하므로 빠지지 않는다.)
   *   accounts  계좌가 여럿인데 기준일이 서로 다르다. 정산은 총액 하나를 그 구간의
   *             예정과 비교하는 일이라, 계좌마다 구간이 다르면 하나로 잴 수 없다.
   *   currency  통화가 섞여 있다. 최소 단위가 달라 애초에 더할 수 없다.
   *   unassigned 계좌가 여럿인데 실제 거래가 어느 계좌의 것인지 몰라, 그 거래가 이미
   *             잔고에 들어 있었는지(또는 새 잔고에 들어 있는지) 가를 수 없다.
   */
  reason: null | 'coverage' | 'accounts' | 'currency' | 'unassigned';
  /** 자료가 있는 가장 이른 날. reason 이 coverage 일 때 어디부터 비는지 알려 준다. */
  coveredFrom: DateISO | null;
  /**
   * reason 이 accounts 일 때 계좌별 기준일. currency 일 때 섞인 통화 목록.
   * unassigned 일 때 판정할 수 없는 실제 거래의 id.
   */
  detail: string[];
  /**
   * 그 사이 지나간 예정과, 이번에 새로 잔고에 들어간 실제 거래.
   *
   * 실제 거래는 예정과 같은 줄에 선다 — 둘 다 "새 잔고에 이만큼 들어 있어야 한다" 의
   * 근거다. 확정된 예정 회차는 이미 빠져 있다(실제 거래가 대신 선다).
   */
  passed: Occurrence[];
  passedIn: number;
  passedOut: number;
  /** 예정대로만 움직였다면 남아 있어야 할 금액. */
  expected: number;
  /** 새로 적은 잔고 − expected. 음수면 예정에 없던 지출. */
  diff: number;
}

export function settle(
  accounts: readonly Account[], entries: readonly Entry[],
  newAmountMinor: number, todayISO: DateISO,
  /** `entries` 가 덮는 가장 이른 날. 주지 않으면 자료가 충분하다고 본다. */
  coveredFrom?: DateISO | null,
  /**
   * 저장할 계좌들 — 새 잔고를 적은 뒤의 모양. 실제 거래 가운데 이번에 **새로** 잔고에
   * 들어가는 것을 고르는 데 쓴다. 주지 않으면 모든 계좌를 오늘 다시 적은 것으로 본다
   * (같은 날이면 반영 목록을 그대로 두고, 아니면 비운다 — `balanceEditor` 와 같은 규칙).
   */
  next?: readonly Account[],
): Settlement {
  // 정산 구간이 비면 occurrences 를 거치지 않고 끝난다. 여기서도 직접 본다.
  assertOriginals(entries);
  /*
    가장 최근에 확인된 잔고 기록을 고른다.

    고르는 순서는 `checkedAt`(순간)으로 정하고 — 하루에 두 번 갈아엎어도 순서가 잡힌다 —
    구간의 경계는 그 기록의 **날짜**(`asOf`)로 잡는다. 예전에는 경계까지 checkedAt 에서
    뽑았는데, `toISOString().slice(0, 10)` 은 UTC 날짜라 한국 시간 오전 9시 이전에 적은
    잔고가 전날로 읽혔다. 그러면 (전날, 오늘] 이 되어 오늘 예정분이 이미 지나간 것으로
    잡힌다.
  */
  const since = balanceAsOf(accounts, todayISO);
  const currentBalance = accounts.reduce((s, a) => s + a.balanceMinor, 0);
  /*
    흡수하는 구간은 **어제까지**다.

    오늘 적어 둔 예정은 아직 일어나지 않은 계획이라 잔고에 흡수됐다고 볼 수 없다.
    한도(`unsettledAfter`)가 오늘 것을 세는 것과 짝이다 — 한쪽이 흡수하고 다른 쪽도 세면
    같은 돈이 두 번 빠진다.
  */
  const planned = occurrences(entries, since, addDaysISO(todayISO, -1));
  /*
    실제 거래는 구간이 아니라 **잔고에 들어 있는가** 가 바뀌는 것으로 센다. 앞 잔고에는
    없었고 새 잔고에는 있는 거래가 이번 정산이 흡수하는 몫이다. 날짜로 자르면 기준일
    당일 거래를 두 번 세거나 놓친다.
  */
  const after = next ?? accounts.map((a) => ({
    ...a,
    asOf: todayISO,
    reflectedEntryIds: normalizeDate(a.asOf) === todayISO ? a.reflectedEntryIds ?? [] : [],
  }));
  const absorbed: Occurrence[] = [];
  for (const entry of entries) {
    if (!isActual(entry) || !participates(entry)) continue;
    const amount = Math.abs(amountOf(entry));
    if (amount === 0) continue;
    if (balanceInclusion(entry, accounts) !== 'in' && balanceInclusion(entry, after) === 'in') {
      absorbed.push({ date: normalizeDate(entry.startDate), entry, amountMinor: amount });
    }
  }
  const passed = [...planned, ...absorbed]
    .sort((a, b) => a.date.localeCompare(b.date) || a.entry.title.localeCompare(b.entry.title));
  const expected = currentBalance + netOf(passed);
  const from = coveredFrom ? normalizeDate(coveredFrom) : null;
  const unknown = [
    ...new Set([...ambiguousActuals(accounts, entries), ...ambiguousActuals(after, entries)]
      .map((e) => e.id)),
  ];

  /*
    확정 차액을 낼 수 없는 세 갈래. 순서대로 본다 — 통화가 섞였으면 나머지는 따질 것도 없다.
  */
  const scope = currencyScopeOf(accounts, entries);
  const asOfs = [...new Set(accounts.map((a) => normalizeDate(a.asOf) || since))].sort();

  let reason: Settlement['reason'] = null;
  let detail: string[] = [];
  if (!scope.ok) {
    reason = 'currency';
    detail = scope.currencies;
  } else if (asOfs.length > 1) {
    // 계좌마다 마지막으로 적은 날이 다르다. 총액 하나로 한 구간을 잴 수 없다.
    reason = 'accounts';
    detail = asOfs;
  } else if (unknown.length > 0) {
    reason = 'unassigned';
    detail = unknown;
  } else if (from && since < from) {
    reason = 'coverage';
    detail = [];
  }

  return {
    since,
    complete: reason === null,
    reason,
    coveredFrom: from || null,
    detail,
    passed,
    passedIn: totalIn(passed),
    passedOut: totalOut(passed),
    expected,
    diff: newAmountMinor - expected,
  };
}

/**
 * 잔고 기준일 이후 ~ 머리 숫자 끝점까지 남은 예정.
 * 기간 예산은 끝점에 걸치면 남은 몫 전체가 담긴다 — limitOn 과 같은 규칙이라야
 * 내역 합과 머리 숫자가 맞는다.
 */
export function upcomingInHorizon(
  accounts: readonly Account[], entries: readonly Entry[], today: DateISO, horizon?: Horizon,
  res: Reservations = {},
): Occurrence[] {
  assertOriginals(entries);
  const h = horizon ?? horizonOf(entries, today);
  // 머리 숫자와 같은 경계에서 세야 목록의 합과 숫자가 맞는다.
  const from = unsettledAfter(accounts, today);
  const budgets = res.budgets ?? NO_BUDGETS;
  const confirmed = confirmedSetOf(entries);
  const out: Occurrence[] = [];
  for (const entry of entries) {
    if (!participates(entry) || isActual(entry)) continue;
    // 예산에서 나가는 지출은 예산 줄에 들어간다. 여기에 또 세우면 두 번 보인다.
    if (inAnyBudget(entry, budgets)) continue;
    if (isSpan(entry) && !entry.recurrence) {
      const start = normalizeDate(entry.startDate);
      const end = normalizeDate(effectiveEndDate(entry));
      // `limitOn` 과 같은 조건이라야 목록의 합과 머리 숫자가 맞는다.
      // 끝점 뒤에 시작하는 기간 예산은 한도를 건드리지 않으므로 목록에도 넣지 않는다.
      if (start && end && start <= h.end) out.push(...occurrences([entry], from, end, confirmed));
    } else {
      out.push(...occurrences([entry], from, h.end, confirmed));
    }
  }
  // 아직 잔고 밖인 실제 거래도 머리 숫자에 들어 있다. 목록에도 같은 조건으로 세운다.
  out.push(...unreflectedActuals(accounts, entries, h.end)
    .filter((o) => !inAnyBudget(o.entry, budgets)));
  out.sort((a, b) => a.date.localeCompare(b.date) || a.entry.title.localeCompare(b.entry.title));
  return out;
}

/**
 * 목록 표시용 요약. 기간 예산의 하루 발생분들은 한 줄로 합친다 —
 * 계산은 일할이지만 사용자에게 쪼갠 숫자를 보여주지 않는다는 약속.
 */
export interface Summary {
  key: string;
  entry: Entry;
  from: DateISO;
  to: DateISO;
  amountMinor: number;
}

export function summarize(list: readonly Occurrence[]): Summary[] {
  const out: Summary[] = [];
  const spans = new Map<string, Summary>();
  for (const o of list) {
    if (isSpan(o.entry) && !o.entry.recurrence) {
      const g = spans.get(o.entry.id);
      if (g) {
        g.to = o.date;
        g.amountMinor += o.amountMinor;
      } else {
        const item = {
          key: o.entry.id, entry: o.entry, from: o.date, to: o.date,
          amountMinor: o.amountMinor,
        };
        spans.set(o.entry.id, item);
        out.push(item);
      }
    } else {
      out.push({
        key: `${o.date}-${o.entry.id}`, entry: o.entry, from: o.date, to: o.date,
        amountMinor: o.amountMinor,
      });
    }
  }
  return out;
}

/**
 * 하루치 (전날, 그날] 구간의 예정과 그 날의 실제 거래.
 *
 * 이 날 무엇이 움직이는가를 보여 주는 자리라 잔고 반영 여부와 무관하게 그 날짜의 실제
 * 거래를 함께 담는다. 확정된 예정 회차는 빠진다 — 실제 거래가 그 자리에 선다.
 */
export function entriesOn(entries: readonly Entry[], date: DateISO): Occurrence[] {
  const day = normalizeDate(date);
  const planned = occurrences(entries, addDaysISO(day, -1), day);
  const actual: Occurrence[] = [];
  for (const entry of entries) {
    if (!isActual(entry) || !participates(entry)) continue;
    const amount = Math.abs(amountOf(entry));
    if (amount > 0 && normalizeDate(entry.startDate) === day) actual.push({ date: day, entry, amountMinor: amount });
  }
  return [...planned, ...actual];
}

/** 이 항목이 tide 흐름에 참여하는가 (컴포넌트에서도 쓴다). */
export function isTideParticipant(entry: Entry): boolean {
  return participates(entry);
}
